import React, { useState } from 'react';
import { useWebSocket } from '../hooks/useWebSocket';
import api from '../services/api';

const WebSocketDemo: React.FC = () => {
  const [inputMessage, setInputMessage] = useState('');

  // Determine WebSocket URL based on environment
  const getWebSocketUrl = () => {
    // Use the same API base as services/api.ts (REACT_APP_API_URL in production/Capacitor);
    // an empty base means same-origin (dev proxy). http(s) -> ws(s).
    const base = (api.defaults.baseURL || window.location.origin).replace(/\/+$/, '');
    return `${base.replace(/^http/, 'ws')}/api/ws`;
  };

  const {
    lastMessage,
    messageHistory,
    sendMessage,
    connectionStatus,
    isConnected,
    clearHistory,
    connect,
    disconnect,
  } = useWebSocket(getWebSocketUrl());

  const handleSendMessage = () => {
    if (inputMessage.trim()) {
      sendMessage({
        type: 'user_message',
        message: inputMessage,
        timestamp: new Date().toISOString(),
      });
      setInputMessage('');
    }
  };

  const getMessageTypeColor = (type: string) => {
    switch (type) {
      case 'connection':
        return 'text-green-600 bg-green-50';
      case 'timestamp':
        return 'text-blue-600 bg-blue-50';
      case 'echo':
        return 'text-purple-600 bg-purple-50';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-800">WebSocket Live Feed</h2>
        <div className="flex items-center space-x-4">
          <div
            className={`px-3 py-1 rounded-full text-sm font-medium ${
              isConnected ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
            }`}
          >
            {connectionStatus}
          </div>
          <div className="flex space-x-2">
            <button
              onClick={connect}
              disabled={isConnected}
              className="px-3 py-1 bg-blue-500 text-white rounded text-sm hover:bg-blue-600 disabled:opacity-50"
            >
              Connect
            </button>
            <button
              onClick={disconnect}
              disabled={!isConnected}
              className="px-3 py-1 bg-red-500 text-white rounded text-sm hover:bg-red-600 disabled:opacity-50"
            >
              Disconnect
            </button>
            <button
              onClick={clearHistory}
              className="px-3 py-1 bg-gray-500 text-white rounded text-sm hover:bg-gray-600"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Last Message Display */}
      {lastMessage && (
        <div className="mb-6 p-4 border-l-4 border-blue-500 bg-blue-50">
          <h3 className="font-semibold text-blue-800 mb-2">Latest Message</h3>
          <div className="text-sm">
            <p>
              <strong>Type:</strong> {lastMessage.type}
            </p>
            <p>
              <strong>Message:</strong> {lastMessage.message}
            </p>
            <p>
              <strong>Time:</strong> {formatTimestamp(lastMessage.timestamp)}
            </p>
            {lastMessage.active_connections && (
              <p>
                <strong>Active Connections:</strong> {lastMessage.active_connections}
              </p>
            )}
          </div>
        </div>
      )}

      {/* Send Message Section */}
      <div className="mb-6 p-4 bg-gray-50 rounded-lg">
        <h3 className="font-semibold text-gray-800 mb-3">Send Message</h3>
        <div className="flex space-x-2">
          <input
            type="text"
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
            placeholder="Type your message..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={!isConnected}
          />
          <button
            onClick={handleSendMessage}
            disabled={!isConnected || !inputMessage.trim()}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Send
          </button>
        </div>
      </div>

      {/* Message History */}
      <div className="border border-gray-200 rounded-lg">
        <div className="p-3 bg-gray-50 border-b border-gray-200">
          <h3 className="font-semibold text-gray-800">Message History ({messageHistory.length})</h3>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {messageHistory.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              No messages yet. Connect to start receiving updates.
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {messageHistory.map((message, index) => (
                <div key={index} className={`p-3 ${getMessageTypeColor(message.type)}`}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="font-medium text-xs uppercase tracking-wide">
                          {message.type}
                        </span>
                        <span className="text-xs text-gray-500">
                          {formatTimestamp(message.timestamp)}
                        </span>
                      </div>
                      <p className="text-sm">{message.message}</p>
                      {message.active_connections && (
                        <p className="text-xs mt-1 opacity-75">
                          Active connections: {message.active_connections}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WebSocketDemo;
